import type { PublicSettings, ThemeTokens } from "@/types";

/**
 * Maps the JSON keys returned by `/api/v1/public/settings.theme` onto the
 * CSS custom properties consumed by `globals.css`.
 *
 * Keeping this mapping in one place lets the AdminThemeEditor stay
 * declarative — it edits keys, not raw CSS — and makes it trivial to add
 * new tokens later.
 */
const TOKEN_TO_CSS_VAR: Record<keyof ThemeTokens, string> = {
  primary:                "--color-primary",
  primary_hover:          "--color-primary-hover",
  primary_soft:           "--color-primary-soft",
  accent:                 "--color-accent",
  accent_hover:           "--color-accent-hover",
  leaf:                   "--color-leaf",
  bg:                     "--color-bg",
  bg_soft:                "--color-bg-soft",
  bg_muted:               "--color-bg-muted",
  text:                   "--color-text",
  text_muted:             "--color-text-muted",
  category_information:   "--color-cat-information",
  category_veranstaltung: "--color-cat-veranstaltung",
  category_ereignis:      "--color-cat-ereignis",
  hero_kicker_color:      "--color-hero-kicker",
  hero_title_color:       "--color-hero-title",
  hero_tagline_color:     "--color-hero-tagline",
};

/**
 * Writes every non-empty theme token into the document root so that
 * Tailwind utilities and editorial CSS pick up the new palette
 * immediately, without a page reload or rebuild.
 */
export function applyThemeFromSettings(settings: PublicSettings | undefined): void {
  if (!settings?.theme || typeof document === "undefined") return;
  const root = document.documentElement;
  for (const [key, cssVar] of Object.entries(TOKEN_TO_CSS_VAR)) {
    const value = settings.theme[key as keyof ThemeTokens];
    if (value && typeof value === "string") {
      root.style.setProperty(cssVar, value);
    }
  }
}
